import { useMemo } from 'react'
import { useInfiniteQuery, useMutation, useQueryClient, type InfiniteData } from '@tanstack/react-query'
import { useAuth } from '@/modules/auth'
import { RangerApi, type RangerSessionPage, type RangerSessionRange, type RangerSessionSummary } from '@/shared/api/ranger'

/** Every list of past chats sits under this, whichever user and filter. */
export const RANGER_SESSIONS_QUERY_KEY = ['ranger', 'sessions'] as const

const SESSIONS_STALE_TIME_MS = 30_000

type SessionPages = InfiniteData<RangerSessionPage>

function useRangerApi() {
  const { getAccessToken } = useAuth()
  return useMemo(() => new RangerApi(getAccessToken), [getAccessToken])
}

/** Past chats for a filter. Keyed by user because the query cache outlives a sign-out. */
export function useRangerSessions(range: RangerSessionRange, options: { enabled?: boolean } = {}) {
  const api = useRangerApi()
  const { user } = useAuth()

  return useInfiniteQuery({
    queryKey: [...RANGER_SESSIONS_QUERY_KEY, user?.id, range],
    queryFn: ({ pageParam }) => api.listSessions(range, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    staleTime: SESSIONS_STALE_TIME_MS,
    enabled: options.enabled ?? true,
  })
}

/** Changes a chat in every cached list right away. The list is refetched afterwards either way. */
function useOptimisticSessionUpdate() {
  const queryClient = useQueryClient()

  return {
    apply: async (change: (sessions: RangerSessionSummary[]) => RangerSessionSummary[]) => {
      await queryClient.cancelQueries({ queryKey: RANGER_SESSIONS_QUERY_KEY })
      queryClient.setQueriesData<SessionPages>({ queryKey: RANGER_SESSIONS_QUERY_KEY }, (data) =>
        data && {
          ...data,
          pages: data.pages.map((page) => ({ ...page, sessions: change(page.sessions) })),
        }
      )
    },
    refetch: () => queryClient.invalidateQueries({ queryKey: RANGER_SESSIONS_QUERY_KEY }),
  }
}

export function useRenameRangerSession() {
  const api = useRangerApi()
  const { apply, refetch } = useOptimisticSessionUpdate()

  return useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) => api.renameSession(id, title),
    onMutate: ({ id, title }) =>
      apply((sessions) => sessions.map((session) => (session.id === id ? { ...session, title } : session))),
    onSettled: refetch,
  })
}

export function useDeleteRangerSession() {
  const api = useRangerApi()
  const { apply, refetch } = useOptimisticSessionUpdate()

  return useMutation({
    mutationFn: (id: string) => api.deleteSession(id),
    onMutate: (id) => apply((sessions) => sessions.filter((session) => session.id !== id)),
    onSettled: refetch,
  })
}
